<nz-page-header nzTitle="Geofence Adjustments">
  <nz-page-header-extra>
    <button nz-button (click)="conflictsQuery.refetch(); requestsQuery.refetch(); summaryQuery.refetch()">Refresh</button>
  </nz-page-header-extra>
</nz-page-header>

<!-- Needs attention: reported and unreported are different numbers and must not be merged. -->
<section class="grid grid-cols-1 gap-4 px-6 sm:grid-cols-3" *ngIf="summaryQuery.data() as summary">
  <div class="rounded-lg border border-base-300 bg-base-100 p-4">
    <p class="text-sm text-base-content/60">Open requests</p>
    <p class="text-2xl font-semibold text-base-content">{{ summary.pendingRequests }}</p>
    <p class="text-xs text-base-content/60">Raised by a tenant who was blocked</p>
  </div>
  <div class="rounded-lg border border-base-300 bg-base-100 p-4">
    <p class="text-sm text-base-content/60">Places in conflict</p>
    <p class="text-2xl font-semibold" [class.text-error]="summary.placesInConflict > 0">{{ summary.placesInConflict }}</p>
    <p class="text-xs text-base-content/60">Overlapping on a live route right now</p>
  </div>
  <div class="rounded-lg border border-base-300 bg-base-100 p-4">
    <p class="text-sm text-base-content/60">Nobody has reported</p>
    <p class="text-2xl font-semibold" [class.text-warning]="summary.unreportedPlaces > 0">{{ summary.unreportedPlaces }}</p>
    <p class="text-xs text-base-content/60">Losing footage with no request open</p>
  </div>
</section>

<div class="px-6 py-4">
  <nz-segmented [nzOptions]="tabs" (nzValueChange)="tab.set($any($event))"></nz-segmented>
</div>

<!-- ---------------------------------------------------------------- conflicts -->
<section class="px-6" *ngIf="tab() === 0">
  <buspro-resources-loading-skeletons *ngIf="conflictsQuery.isPending()" [loading]="true"></buspro-resources-loading-skeletons>

  <buspro-empty-state
    *ngIf="!conflictsQuery.isPending() && conflicts().length === 0"
    icon="check"
    accent="success"
    size="page"
    title="No overlapping stop zones"
    description="Every stop on every active route has clear air around it. Nothing is losing footage to an overlap.">
  </buspro-empty-state>

  <div *ngFor="let conflict of conflicts()" class="mb-3 rounded-lg border border-base-300 bg-base-100 p-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p class="font-semibold text-base-content">
          {{ conflict.placeName }}
          <nz-tag>{{ conflict.placeType }}</nz-tag>
          <nz-tag *ngIf="conflict.isLikelyCulprit" nzColor="red" nz-tooltip
                  nzTooltipTitle="This is the wider zone in at least one of these conflicts. Fixing it usually clears the others too, and avoids shrinking a neighbour that was never the problem.">
            widest zone here
          </nz-tag>
        </p>
        <p class="text-sm text-base-content/60">
          {{ conflict.currentRadius }}m today ·
          {{ conflict.conflicts.length }} conflict(s) across {{ conflict.routesAffected }} route(s)
        </p>
      </div>

      <div class="flex items-center gap-2">
        <!-- The honest case: shrinking this place alone cannot fix it. Never show a number. -->
        <span *ngIf="conflict.recommendedRadius === null" class="text-sm text-error" nz-tooltip
              nzTooltipTitle="No radius above the usable floor clears every conflict. The neighbour has to shrink too, or one of the two stops should come off the route.">
          Needs {{ conflict.blockedByPartner }} to change too
        </span>

        <!-- Opacity tints must be a whole literal string: a `/` inside a [class.x] binding
             name fails the Angular parser (NG5002). -->
        <span *ngIf="conflict.recommendedRadius !== null" class="text-sm"
              [ngClass]="conflict.isLikelyCulprit ? 'text-base-content' : 'text-base-content/50'">
          Set to <strong>{{ conflict.recommendedRadius }}m</strong>
        </span>

        <!-- Shrinking the narrower stop "works" but punishes the wrong one. Say so. -->
        <span *ngIf="betterTarget(conflict) as better" class="text-sm text-warning" nz-tooltip
              [nzTooltipTitle]="'Fixing ' + better + ' clears this conflict without shrinking this stop.'">
          adjust {{ better }} instead
        </span>

        <a nz-button nzType="default" [href]="mapLink(conflict)" target="_blank" rel="noopener">View on map</a>

        <ng-container *ngIf="openRequestByPlace().get(conflict.standardPlaceId) as open; else raiseBtn">
          <button nz-button nzType="primary" [disabled]="open.recommendedRadius === null"
                  [nzLoading]="applyMutation.isPending()" (click)="onApply(open)">
            Apply change
          </button>
        </ng-container>
        <ng-template #raiseBtn>
          <button nz-button [nzLoading]="raiseMutation.isPending()" (click)="onRaise(conflict)">Raise request</button>
        </ng-template>
      </div>
    </div>

    <ul class="mt-3 space-y-1">
      <li *ngFor="let pair of conflict.conflicts" class="text-sm text-base-content/70">
        <span class="text-error">overlaps {{ pair.overlapMetres }}m</span>
        — “{{ pair.a }}” ({{ pair.aRadius }}m) and “{{ pair.b }}” ({{ pair.bRadius }}m) are
        {{ pair.metresApart }}m apart
        <span class="text-base-content/50">· on {{ pair.routeNames.length }} route(s)</span>
      </li>
    </ul>
  </div>
</section>

<!-- ----------------------------------------------------------------- requests -->
<section class="px-6" *ngIf="tab() === 1">
  <buspro-resources-loading-skeletons *ngIf="requestsQuery.isPending()" [loading]="true"></buspro-resources-loading-skeletons>

  <buspro-empty-state
    *ngIf="!requestsQuery.isPending() && requests().length === 0"
    icon="inbox"
    size="page"
    title="No adjustment requests"
    description="When a tenant is refused a route save because two stop zones overlap, their request lands here.">
  </buspro-empty-state>

  <div *ngFor="let request of requests()" class="mb-3 rounded-lg border border-base-300 bg-base-100 p-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p class="font-semibold text-base-content">
          {{ request.placeName }}
          <nz-tag [nzColor]="request.status === 'PENDING' ? 'orange' : request.status === 'APPLIED' ? 'green' : 'default'">
            {{ request.status }}
          </nz-tag>
        </p>
        <p class="text-sm text-base-content/60">
          {{ request.currentRadius }}m
          <ng-container *ngIf="request.recommendedRadius !== null">→ {{ request.recommendedRadius }}m</ng-container>
          <ng-container *ngIf="request.requesterName"> · asked by {{ request.requesterName }}</ng-container>
          <ng-container *ngIf="request.routeName"> while saving {{ request.routeName }}</ng-container>
          <ng-container *ngIf="request.source === 'ADMIN_SWEEP'"> · found by sweep</ng-container>
        </p>
        <p *ngIf="request.status === 'APPLIED'" class="text-sm text-success">
          Applied at {{ request.appliedRadius }}m
        </p>
        <p *ngIf="request.status === 'SUPERSEDED'" class="text-sm text-base-content/60">
          The conflict was already gone when this was applied — nothing was changed.
        </p>
      </div>

      <div class="flex items-center gap-2" *ngIf="request.status === 'PENDING'">
        <button nz-button nzType="primary" [disabled]="request.recommendedRadius === null"
                [nzLoading]="applyMutation.isPending()" (click)="onApply(request)">
          Apply change
        </button>
        <button nz-button nzDanger (click)="onReject(request)">Reject</button>
      </div>
    </div>
  </div>
</section>
